import { useEffect, useMemo, useRef, useState } from 'react';
import { Eye, FileText, Loader2, Pencil, Plus, Trash2, Upload, X } from 'lucide-react';
import { Button } from '@/shared/components/ui/button';
import { Combobox } from '@/shared/components/ui/combobox';
import { DatePicker } from '@/shared/components/ui/date-picker';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/components/ui/dialog';
import { Input } from '@/shared/components/ui/input';
import { Label } from '@/shared/components/ui/label';
import { cn } from '@/shared/lib/cn';
import { formatMoney, parseMoney } from '@/shared/lib/money';
import { esCajaEfectivoConPar } from '@/shared/lib/cajasEfectivo';
import { useActiveCountry } from '@/shared/countries/ActiveCountryProvider';
import type { MedioDePagoItem } from '@/shared/api/controllers/mediosDePago';
import { fmtPct } from '@/shared/api/controllers/mpCuotas';
import type { Moneda } from './MoneyCell';
import { MoneyInput } from './MoneyInput';
import { FiscalDataMissingWarning } from './FiscalDataMissingWarning';

/**
 * Multi-pago dialog. Mirrors PowerApps's `Collect_SubirPagos` flow
 * (`Screen_Pagos.pa.yaml`): the user fills the form for one pago,
 * clicks "+ Agregar" to stage it in a local queue, then repeats for
 * additional pagos before clicking Registrar to commit the entire
 * batch. Each staged pago carries an optional comprobante file.
 *
 * The parent owns the actual submit — for each entry it calls the
 * `registrarComprobante` endpoint once, then uploads the file via
 * `entityType='ComprobanteVenta'`. Atomic per-row, not per-batch:
 * a single row failing surfaces a toast but the others still land.
 */
export type StagedPago = {
  /** Local key — only meaningful inside the dialog. */
  key: string;
  medioPago: string;
  medioPagoId: string;
  categoria: 'Local' | 'Internacional';
  monto: number;
  tipoCambio?: number;
  pesos?: number;
  fechaCobro: string; // dd/mm/yyyy
  fechaPago: string;  // dd/mm/yyyy
  esPagoFinal: boolean;
  contieneFactura: boolean;
  /** AR: pedir autorización para emitir la factura ANTES de cobrar. No
   *  cambia el alta del comprobante — dispara una solicitud a Aprobaciones. */
  facturacionPrevia?: boolean;
  /** Optional comprobante file. Single per pago, matching SHP schema. */
  file: File | null;
  /** Caja E-cheq: datos del cheque (queda pendiente de autorización). */
  echeq?: EcheqDatos;
};

export type EcheqDatos = {
  nroCheque: string;
  bancoEmisor: string;
  cuitLibrador: string;
  /** yyyy-mm-dd: desde cuándo se puede cobrar. */
  fechaPago: string;
};

const CUIT_RE = /^\d{2}-?\d{8}-?\d$/;

const ALLOWED_MIMES = ['application/pdf', 'image/png', 'image/jpeg'];
const MAX_BYTES = 4 * 1024 * 1024;

export function RegistrarPagoDialog({
  open,
  kind,
  ventaMoneda,
  mediosDePago,
  totalACobrar,
  disponibleAFacturar,
  disponibleSinFactura,
  recargoMp = null,
  isExento = false,
  clienteFiscalMissing = null,
  clienteCuit = '',
  loading = false,
  onSubmit,
  onCancel,
}: {
  open: boolean;
  kind: 'pago' | 'promesa';
  ventaMoneda: Moneda;
  mediosDePago: MedioDePagoItem[];
  totalACobrar: number;
  /** Remaining amount that can still be invoiced for this venta, in
   *  venta currency. Used to gate the "Lleva Factura" toggle so the
   *  user can't stage a pago whose factura portion would exceed what's
   *  still pending to invoice. */
  disponibleAFacturar: number;
  /** Lo que todavía se puede cobrar SIN factura, en moneda de la venta
   *  (`montoCobroEfectivo` menos los cobros sin factura ya cargados). En 0
   *  —venta al 100%— todo pago lleva factura: el toggle queda tildado y
   *  trabado para cualquier medio. */
  disponibleSinFactura: number;
  /** Recargo MercadoPago por cuotas guardado en la venta: se muestra al
   *  elegir la caja con cuotas (ya está sumado al total a cobrar). */
  recargoMp?: { cuotas: number; pct: number; monto: number } | null;
  /** Client is IVA Exento. Then `disponibleAFacturar` equals the venta total,
   *  so an over-limit means the monto exceeds the VENTA TOTAL — show the
   *  generic "Monto excedido" wording instead of the factura-specific one. */
  isExento?: boolean;
  /** AR only: receptor fiscal fields (condición IVA / CUIT) the vendedor still
   *  has to complete on the contacto before this client can be facturado.
   *  Non-null → a warning shows when "Lleva Factura" is on. Facturación later
   *  hard-blocks the CAE on the same gap. */
  clienteFiscalMissing?: string[] | null;
  /** CUIT del cliente: valor inicial del CUIT librador de un E-cheq. */
  clienteCuit?: string;
  loading?: boolean;
  /** Called with the full queue of staged pagos when the user hits Registrar. */
  onSubmit: (pagos: StagedPago[]) => void;
  onCancel: () => void;
}) {
  const isPromesa = kind === 'promesa';
  const title = isPromesa ? 'Registrar promesa de pago' : 'Registrar pago';

  const [queue, setQueue] = useState<StagedPago[]>([]);

  // Form state — these are the inputs the user is currently editing.
  // Pushing to the queue resets them (mirrors PowerApps's
  // `Reset(Cbox_Metodo_P)` etc.) so the next pago starts blank.
  const [medio, setMedio] = useState<MedioDePagoItem | null>(null);
  const [monto, setMonto] = useState('');
  const [usaTotal, setUsaTotal] = useState(false);
  const [tipoCambio, setTipoCambio] = useState('');
  const [fechaPago, setFechaPago] = useState(todayIso);
  const [esPagoFinal, setEsPagoFinal] = useState(false);
  const [contieneFactura, setContieneFactura] = useState(false);
  const [facturacionPrevia, setFacturacionPrevia] = useState(false);
  const [echeq, setEcheq] = useState<EcheqDatos>({ nroCheque: '', bancoEmisor: '', cuitLibrador: '', fechaPago: '' });
  const [file, setFile] = useState<File | null>(null);
  const [fileError, setFileError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  // When non-null, the next file pick replaces that staged pago's file
  // instead of setting the in-progress form's file. Reset after each
  // pick so subsequent uploads target the form again.
  const replacingKeyRef = useRef<string | null>(null);
  const [preview, setPreview] = useState<File | null>(null);

  // Reset everything when the dialog reopens.
  useEffect(() => {
    if (!open) return;
    setQueue([]);
    resetForm();
  }, [open]);

  const resetForm = () => {
    setMedio(null);
    setMonto('');
    setUsaTotal(false);
    setTipoCambio('');
    setFechaPago(todayIso());
    setEsPagoFinal(false);
    setContieneFactura(false);
    setFacturacionPrevia(false);
    setEcheq({ nroCheque: '', bancoEmisor: '', cuitLibrador: clienteCuit, fechaPago: '' });
    setFile(null);
    setFileError(null);
  };

  // Local medios are denominated in the active country's home currency
  // (ARS / MXN). Hardcoding 'ARS' here mis-flagged every MX Local medio
  // as cross-currency vs an MXN venta.
  const { config: activeCountry } = useActiveCountry();
  const homeCurrency = activeCountry.currency.primary;
  // MX moves the Anticipada/Final decision from registration to factura
  // upload (FacturarDialog on the Administración side carries the
  // toggle). At registration time MX rows are always recorded as
  // `esPagoFinal=false` — the operator picks the stage when the
  // factura PDF is attached. AR keeps the original Pago Final checkbox.
  const isMx = activeCountry.code === 'MX';
  const categoria = medio?.fields.Categoria_MDP === 'Internacional' ? 'Internacional' : 'Local';
  const medioMoneda: Moneda = categoria === 'Internacional' ? 'USD' : homeCurrency;
  const needsTC = medio !== null && medioMoneda !== ventaMoneda;
  // Per-row "force Lleva Factura" flag, set by the Configuración CRUD
  // (typically true for Banco rows except Morvalab). When true the
  // toggle is auto-checked and locked — the user can't desmarcar to
  // bypass the disponible-a-facturar guard.
  // Excepción: el par de efectivo. Ahí "Lleva factura" es la etiqueta de la
  // caja, no un candado — el toggle queda libre y decide si el cobro cae en
  // "Efectivo Pesos" o en "Efectivo Pesos N" (lib/cajasEfectivo).
  const medioFuerzaFactura =
    medio?.fields.LlevaFactura_MDP === 'SI' &&
    !esCajaEfectivoConPar(medio.fields.Medio_MDP ?? '');

  // Cupo sin factura, descontando los pagos sin factura ya en la cola. Sin
  // cupo (venta al 100%, o ya se cobró toda la parte sin factura) el pago
  // lleva factura con cualquier medio: si no, el efectivo caía en la caja N
  // y la venta quedaba facturada de menos. El server rechaza igual el 100%.
  const queuedSinFacturaInVenta = useMemo(() => {
    let total = 0;
    for (const p of queue) {
      if (p.contieneFactura) continue;
      total += p.pesos != null ? p.pesos : p.monto;
    }
    return total;
  }, [queue]);
  const remainingSinFactura = Math.max(
    0,
    round2(disponibleSinFactura - queuedSinFacturaInVenta),
  );
  const ventaExigeFactura = remainingSinFactura <= 0.005;
  const forceLlevaFactura = medioFuerzaFactura || ventaExigeFactura;

  // Auto-sync contieneFactura with the picked medio's policy. Switching
  // from a Banco (forced) to Efectivo (free) clears the toggle; switching
  // to a Banco re-arms it. The user can still uncheck for non-forced
  // medios if they don't want a factura on this particular pago.
  useEffect(() => {
    // `facturacionPrevia` depende de `contieneFactura`, así que cae con él.
    // Sin esto quedaba tildado-pero-deshabilitado al pasar a un medio sin
    // factura, y se re-armaba solo si el usuario volvía a tildar Lleva
    // Factura — mandando una solicitud que ya no había pedido.
    if (!medio) {
      setContieneFactura(false);
      setFacturacionPrevia(false);
      return;
    }
    setContieneFactura(forceLlevaFactura);
    if (!forceLlevaFactura) setFacturacionPrevia(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [medio?.id, forceLlevaFactura]);

  // Clear `monto` + the "Total" checkbox when the medio's currency
  // CHANGES (ARS ↔ USD), but keep them on same-currency switches.
  // Rationale: a typed/auto-filled amount in one currency would
  // silently re-interpret in the other (5000 ARS → "5000 USD" is
  // wrong); but switching Efectivo Pesos → Transferencia Galicia
  // (both ARS) is a method swap, not a fresh form, so the amount
  // should survive. Also closes two edges around the disabled
  // Total button: Total ticked on non-USD then switch to USD-cross-
  // currency leaves the checkbox visually ticked AND the stale ARS
  // amount filled, in both the still-checked and unchecked-then-
  // switched paths.
  const prevMedioMonedaRef = useRef<'ARS' | 'MXN' | 'USD' | null>(null);
  useEffect(() => {
    if (!medio) {
      prevMedioMonedaRef.current = null;
      return;
    }
    const prev = prevMedioMonedaRef.current;
    prevMedioMonedaRef.current = medioMoneda;
    if (prev !== null && prev !== medioMoneda) {
      setUsaTotal(false);
      setMonto('');
    }
    // Always clear the tipo de cambio on a medio swap — it's contextual
    // to the medio (and to the user's intent for THIS particular pago),
    // not a session-level constant. Without this, a TC entered for a
    // cross-currency medio survived after switching to a same-currency
    // medio (where the field is disabled but still holds state) and
    // re-appeared when the user later swapped back to another cross-
    // currency medio.
    setTipoCambio('');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [medio?.id, medioMoneda]);
  const montoNum = parseMoney(monto) ?? NaN;
  const tcNum = parseMoney(tipoCambio) ?? NaN;

  const equivalente = useMemo(() => {
    if (!Number.isFinite(montoNum)) return undefined;
    if (!needsTC) return montoNum;
    if (!Number.isFinite(tcNum) || tcNum <= 0) return undefined;
    // Convention: `tipoCambio` is always entered as "local per USD".
    // When the medio is USD (Internacional), multiply to get the local
    // equivalent. When the medio is the local currency (Local), divide
    // to get the USD equivalent. Previously this only matched 'ARS'
    // explicitly, so an MX (MXN ↔ USD) pair fell through both branches
    // and the Equivalente field stayed empty.
    if (medioMoneda === 'USD') return montoNum * tcNum;
    return montoNum / tcNum;
  }, [montoNum, tcNum, needsTC, medioMoneda]);

  const equivalenteLabel = needsTC
    ? `Equivalente (${ventaMoneda})`
    : ventaMoneda === 'USD'
      ? 'Dolares'
      : 'Pesos';

  // Sum already-staged pagos converted into venta-currency. When `pesos`
  // is set the medio was cross-currency and we use the conversion;
  // otherwise the medio matched the venta and `monto` is already in
  // venta currency.
  const queuedTotalInVenta = useMemo(() => {
    let total = 0;
    for (const p of queue) {
      total += p.pesos != null ? p.pesos : p.monto;
    }
    return total;
  }, [queue]);

  // What "Total" should fill: the original target minus what's already
  // queued, so successive Total clicks across multiple staged pagos
  // never re-suggest the full amount.
  const remainingTotal = Math.max(0, round2(totalACobrar - queuedTotalInVenta));

  const handleTotalToggle = (next: boolean) => {
    setUsaTotal(next);
    if (next && remainingTotal > 0 && !needsTC) {
      setMonto(String(remainingTotal));
    }
  };

  // E-cheq: los datos del cheque son obligatorios (y sólo como pago).
  const esEcheq = medio?.fields.Tipo_MDP === 'E-cheq';
  // Mismo N° + CUIT ya en la lista de este diálogo (el server sólo compara
  // contra lo ya guardado, de a un pago por vez).
  const soloDigitos = (c: string) => c.replace(/\D/g, '');
  const echeqRepetido =
    esEcheq &&
    echeq.nroCheque.trim() !== '' &&
    queue.some(
      (q) =>
        q.echeq?.nroCheque === echeq.nroCheque.trim() &&
        soloDigitos(q.echeq.cuitLibrador) === soloDigitos(echeq.cuitLibrador),
    );
  const echeqOk =
    !esEcheq ||
    (!echeqRepetido &&
      echeq.nroCheque.trim() !== '' &&
      echeq.bancoEmisor.trim() !== '' &&
      CUIT_RE.test(echeq.cuitLibrador.trim()) &&
      echeq.fechaPago >= todayIso(-30) &&
      echeq.fechaPago <= todayIso(365));
  const echeqStaged = esEcheq
    ? { ...echeq, nroCheque: echeq.nroCheque.trim(), bancoEmisor: echeq.bancoEmisor.trim(), cuitLibrador: echeq.cuitLibrador.trim() }
    : undefined;

  const canStage =
    echeqOk &&
    medio !== null &&
    Number.isFinite(montoNum) &&
    montoNum > 0 &&
    Boolean(fechaPago) &&
    (!needsTC || (Number.isFinite(tcNum) && tcNum > 0)) &&
    // Promesas don't carry a comprobante at registration (the file is
    // attached later in Carga de Pagos); pagos do require it.
    (isPromesa || file !== null);

  // canRegistrar / canStage gates exclude excedido — defined just below
  // once `excedido` itself is computed. Both the "+ Agregar" and the
  // dialog's primary "Registrar" button rely on the gated form being
  // valid, so a single excedido state blocks both paths uniformly.

  const handleAdd = () => {
    if (!medio || !canStage) return;
    const staged: StagedPago = {
      key: cryptoKey(),
      medioPago: medio.fields.Medio_MDP?.trim() || '',
      medioPagoId: medio.id,
      categoria,
      monto: montoNum,
      tipoCambio: needsTC ? tcNum : undefined,
      pesos: needsTC ? equivalente : undefined,
      fechaCobro: isoToAr(fechaPago),
      fechaPago: isoToAr(fechaPago),
      // MX never sets Pago Final at registration — the
      // Anticipada/Final decision happens at factura upload time
      // (FacturarDialog). AR keeps the form's selection.
      esPagoFinal: isMx ? false : esPagoFinal,
      contieneFactura,
      facturacionPrevia: !isMx && contieneFactura && facturacionPrevia,
      file,
      echeq: echeqStaged,
    };
    setQueue((prev) => [...prev, staged]);
    resetForm();
  };

  const removeStaged = (key: string) => {
    setQueue((prev) => prev.filter((p) => p.key !== key));
  };

  const handleRegistrar = () => {
    // Block submit while the in-progress form is excedido — same gate
    // the Registrar button shows so a stale Enter / keyboard submit
    // doesn't smuggle past the disponible-a-facturar guard.
    if (excedido || excedidoSinFactura) return;
    // Allow registering the form contents directly without forcing the
    // user to click "+" first, when only one pago is needed.
    let final = queue;
    if (canStage) {
      const staged: StagedPago = {
        key: cryptoKey(),
        medioPago: medio!.fields.Medio_MDP?.trim() || '',
        medioPagoId: medio!.id,
        categoria,
        monto: montoNum,
        tipoCambio: needsTC ? tcNum : undefined,
        pesos: needsTC ? equivalente : undefined,
        fechaCobro: isoToAr(fechaPago),
        fechaPago: isoToAr(fechaPago),
        // See handleAdd: MX defers the Pago Final decision to the
        // factura upload step, so the registration payload always
        // pins it to false.
        esPagoFinal: isMx ? false : esPagoFinal,
        contieneFactura,
        facturacionPrevia: !isMx && contieneFactura && facturacionPrevia,
        file,
        echeq: echeqStaged,
      };
      final = [...queue, staged];
    }
    if (final.length === 0) return;
    onSubmit(final);
  };

  const triggerFilePick = () => {
    if (loading) return;
    setFileError(null);
    replacingKeyRef.current = null;
    fileInputRef.current?.click();
  };

  const triggerReplaceStagedFile = (key: string) => {
    if (loading) return;
    setFileError(null);
    replacingKeyRef.current = key;
    fileInputRef.current?.click();
  };

  const onFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const picked = e.target.files?.[0] ?? null;
    const replaceKey = replacingKeyRef.current;
    replacingKeyRef.current = null;
    e.target.value = '';
    if (!picked) return;
    if (!ALLOWED_MIMES.includes(picked.type)) {
      setFileError(`Tipo no soportado: solo PDF, PNG o JPG.`);
      return;
    }
    if (picked.size > MAX_BYTES) {
      setFileError(`El archivo supera los 4 MB.`);
      return;
    }
    if (replaceKey) {
      setQueue((prev) =>
        prev.map((p) => (p.key === replaceKey ? { ...p, file: picked } : p)),
      );
    } else {
      setFile(picked);
    }
  };

  // Lleva Factura guard. Mirrors PowerApps' "monto excedido" warning on
  // PopUp_AdvertenciaPromeMontoDisponible: when the user toggles ON the
  // factura checkbox, the pago's amount in venta currency cannot exceed
  // what's still left to invoice — minus whatever already-staged pagos
  // in the queue have factura too.
  const queuedConFacturaInVenta = useMemo(() => {
    let total = 0;
    for (const p of queue) {
      if (!p.contieneFactura) continue;
      total += p.pesos != null ? p.pesos : p.monto;
    }
    return total;
  }, [queue]);
  const remainingFactura = Math.max(
    0,
    round2(disponibleAFacturar - queuedConFacturaInVenta),
  );

  // Live derived check: when contieneFactura is on AND the in-progress
  // monto can be expressed in venta currency, refuse anything over what
  // is still pending to invoice. Replaces the older "uncheck and warn"
  // behavior — keeping the toggle stable lets the user see exactly what
  // the form thinks they're doing while the Add / Registrar buttons
  // stay blocked until the monto fits.
  const valueInVentaForFactura =
    contieneFactura
      ? needsTC
        ? equivalente
        : Number.isFinite(montoNum)
          ? montoNum
          : undefined
      : undefined;
  const excedido =
    valueInVentaForFactura != null &&
    valueInVentaForFactura > remainingFactura + 0.005;

  // Espejo para lo que va SIN factura: no puede pasarse del cupo sin factura
  // que queda. Sólo aplica con cupo > 0 — sin cupo el toggle está trabado.
  const valueInVentaSinFactura =
    !contieneFactura
      ? needsTC
        ? equivalente
        : Number.isFinite(montoNum)
          ? montoNum
          : undefined
      : undefined;
  const excedidoSinFactura =
    valueInVentaSinFactura != null &&
    valueInVentaSinFactura > remainingSinFactura + 0.005;

  // "Registrar" is enabled when there's something to send — either at
  // least one staged pago OR the in-progress form is fillable AND the
  // in-progress form isn't currently excedido (the queue itself can
  // never carry an excedido pago because we block staging earlier).
  // Fiscal gate: a pago that lleva factura can't be staged/registered while
  // the client's fiscal data is incomplete — Facturación couldn't emit its CAE
  // and only the vendedor can complete it. Escapable by unchecking Lleva
  // Factura. The queue check covers a pago staged before the contacto loaded.
  const fiscalMissing = (clienteFiscalMissing?.length ?? 0) > 0;
  const fiscalBlockedForm = contieneFactura && fiscalMissing;
  const fiscalBlockedQueue = fiscalMissing && queue.some((p) => p.contieneFactura);
  const canRegistrar =
    !excedido &&
    !excedidoSinFactura &&
    !fiscalBlockedForm &&
    !fiscalBlockedQueue &&
    (queue.length > 0 || canStage);
  const canStageButton = canStage && !excedido && !excedidoSinFactura && !fiscalBlockedForm;

  const handleFacturaToggle = (next: boolean) => {
    // Forced rows (Banco with lleva_factura=true, or a venta sin cupo sin
    // factura) ignore unchecks. The CRUD is the only place to flip the policy.
    if (forceLlevaFactura && !next) return;
    setContieneFactura(next);
    // Sin factura no hay nada que facturar por adelantado: la cola de
    // Facturación filtra por ContieneFactura, así que la fila nunca
    // aparecería para emitir.
    if (!next) setFacturacionPrevia(false);
  };

  return (
    <Dialog open={open} onOpenChange={(next) => !next && !loading && onCancel()}>
      <DialogContent
        hideClose={loading}
        // La promesa son cinco campos y nada más: a 2xl quedaba medio diálogo
        // vacío. El pago sí se queda ancho — abajo lleva la carga del
        // comprobante y la lista de pagos en cola, que necesitan el espacio.
        className={cn(isPromesa ? 'max-w-lg' : 'max-w-2xl')}
        processing={loading && 'Registrando pago…'}
      >
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
        </DialogHeader>
        <DialogBody className="space-y-4">
          {/* Row 1 — Fecha + Metodo */}
          <div className="grid grid-cols-2 gap-3">
            <Field label="Fecha">
              <DatePicker
                value={fechaPago}
                onChange={setFechaPago}
                ariaLabel="Fecha de pago"
                clearable={false}
              />
            </Field>
            <Field label="Método">
              <Combobox
                value={medio}
                onChange={setMedio}
                // Un E-cheq ya existe: se registra como pago, nunca como promesa.
                items={isPromesa ? mediosDePago.filter((m) => m.fields.Tipo_MDP !== 'E-cheq') : mediosDePago}
                getKey={(m) => m.id}
                getLabel={(m) => m.fields.Medio_MDP?.trim() || '—'}
                getSublabel={(m) => (m.fields.Categoria_MDP === 'Internacional' ? 'USD' : homeCurrency)}
                matcher={(m, q) =>
                  (m.fields.Medio_MDP ?? '').toLowerCase().includes(q) ||
                  (m.fields.Categoria_MDP === 'Internacional' ? 'usd' : homeCurrency.toLowerCase()).includes(q)
                }
                placeholder="Seleccionar Método"
              />
            </Field>
          </div>

          {recargoMp && medio?.fields.Cuotas_MDP === 'SI' && (
            <div className="rounded-lg border border-sky-200 bg-sky-50 px-3 py-2 text-sm text-sky-900 dark:border-sky-900 dark:bg-sky-950/30 dark:text-sky-100">
              <span className="font-medium">
                {recargoMp.cuotas} cuotas · recargo MercadoPago +{fmtPct(recargoMp.pct)}
              </span>{' '}
              ({formatMoney(recargoMp.monto, ventaMoneda)}), ya incluido en el total a cobrar.
            </div>
          )}

          {/* Se despliega (alto + opacidad) en vez de aparecer de golpe. Queda
              montado: `inert` lo saca del foco y del lector de pantalla cerrado. */}
          <div
            className={cn(
              '!mt-0 grid transition-[grid-template-rows,opacity] duration-200 ease-out',
              esEcheq ? 'grid-rows-[1fr] opacity-100' : 'grid-rows-[0fr] opacity-0',
            )}
            {...(!esEcheq && { inert: '' })}
          >
            <div className="min-h-0 overflow-hidden">
              <div className="pt-4">
                <div className="space-y-2 rounded-xl border bg-muted/40 p-3">
                  <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
                    <Field label="N° de cheque">
                      <Input
                        variant="filled"
                        value={echeq.nroCheque}
                        onChange={(e) => setEcheq((x) => ({ ...x, nroCheque: e.target.value }))}
                        aria-label="N° de cheque"
                        className="h-10"
                        disabled={loading}
                      />
                    </Field>
                    <Field label="Banco emisor">
                      <Input
                        variant="filled"
                        value={echeq.bancoEmisor}
                        onChange={(e) => setEcheq((x) => ({ ...x, bancoEmisor: e.target.value }))}
                        aria-label="Banco emisor"
                        className="h-10"
                        disabled={loading}
                      />
                    </Field>
                    <Field label="CUIT librador">
                      <Input
                        variant="filled"
                        inputMode="numeric"
                        value={echeq.cuitLibrador}
                        onChange={(e) => setEcheq((x) => ({ ...x, cuitLibrador: e.target.value }))}
                        aria-label="CUIT librador"
                        className="h-10"
                        placeholder="20-12345678-9"
                        disabled={loading}
                      />
                    </Field>
                    <Field label="Fecha de pago">
                      <DatePicker
                        value={echeq.fechaPago}
                        onChange={(v) => setEcheq((x) => ({ ...x, fechaPago: v }))}
                        ariaLabel="Fecha de pago del cheque"
                        placeholder="dd/mm/aaaa"
                        // Presentable (30 días desde la fecha de pago) y hasta un año adelante.
                        min={todayIso(-30)}
                        max={todayIso(365)}
                        clearable={false}
                      />
                    </Field>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    Un administrador tiene que autorizar el E-cheq. El pago se confirma cuando se
                    acredita en el banco.
                    {echeq.cuitLibrador.trim() !== '' && !CUIT_RE.test(echeq.cuitLibrador.trim()) && (
                      <span className="ml-1 font-medium text-destructive">CUIT inválido.</span>
                    )}
                    {echeqRepetido && (
                      <span className="ml-1 font-medium text-destructive">
                        Ese E-cheq ya está en la lista.
                      </span>
                    )}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Row 2 — Monto / TC / Equivalente */}
          <div className="grid grid-cols-3 gap-3">
            <Field
              label={
                <span className="flex items-center gap-2">
                  Monto
                  <button
                    type="button"
                    role="checkbox"
                    aria-checked={usaTotal}
                    onClick={() => handleTotalToggle(!usaTotal)}
                    disabled={loading || needsTC || remainingTotal <= 0}
                    className={cn(
                      'inline-flex items-center gap-1 text-xs font-normal disabled:opacity-50',
                      usaTotal ? 'text-emerald-700' : 'text-muted-foreground',
                    )}
                    title={
                      needsTC
                        ? 'Disponible solo cuando el medio coincide con la moneda de la venta'
                        : remainingTotal <= 0
                          ? 'Los pagos cargados ya cubren el total'
                          : queue.length > 0
                            ? 'Usar lo que falta del total'
                            : 'Usar el total a cobrar'
                    }
                  >
                    <span
                      className={cn(
                        'flex h-4 w-4 items-center justify-center rounded border',
                        usaTotal
                          ? 'border-emerald-500 bg-emerald-500 text-white'
                          : 'border-border bg-background',
                      )}
                    >
                      {usaTotal ? '✓' : ''}
                    </span>
                    Total
                  </button>
                </span>
              }
            >
              <MoneyInput
                variant="filled"
                value={monto}
                moneda={medioMoneda}
                onChange={(next) => {
                  setMonto(next);
                  setUsaTotal(false);
                }}
                className="h-10 text-right"
                disabled={loading}
              />
            </Field>
            <Field label="Tipo de cambio">
              <Input
                type="text"
                inputMode="decimal"
                variant="filled"
                value={tipoCambio}
                onChange={(e) => setTipoCambio(sanitizeDecimal(e.target.value))}
                className="h-10 text-right"
                disabled={loading || !needsTC}
                placeholder={needsTC ? '' : '—'}
              />
            </Field>
            <Field label={equivalenteLabel}>
              <Input
                variant="filled"
                value={equivalente != null ? formatMoney(equivalente, ventaMoneda) : ''}
                readOnly
                disabled
                className="h-10 text-right"
                placeholder="—"
              />
            </Field>
          </div>

          {/* Row 3 — flags en una sola fila, repartidas a lo ancho. Apiladas
              al costado de los montos apretaban los inputs y se leían como
              una columna aparte; acá quedan como lo que son: tres opciones
              del mismo pago. Envuelven en pantallas angostas. */}
          <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-2">
            {!isMx && (
              <CheckboxRow
                checked={esPagoFinal}
                onChange={setEsPagoFinal}
                label="Pago Final"
                disabled={loading}
              />
            )}
            <CheckboxRow
              checked={contieneFactura}
              onChange={handleFacturaToggle}
              label="Lleva Factura"
              // Forced by the medio's policy (Banco rows with
              // lleva_factura=true) or by a venta without cupo sin factura.
              // The Pagos UI can't bypass either.
              disabled={loading || forceLlevaFactura}
              title={
                medioFuerzaFactura
                  ? 'Este método de pago debe llevar factura de manera obligatoria'
                  : ventaExigeFactura
                    ? 'Esta venta no tiene saldo para cobrar sin factura'
                    : undefined
              }
            />
            {/* AR-only: en MX la factura es CFDI y no existe esta cola. */}
            {!isMx && (
              <CheckboxRow
                checked={facturacionPrevia}
                onChange={setFacturacionPrevia}
                label="Factura Anticipada"
                disabled={loading || !contieneFactura}
                title={
                  !contieneFactura
                    ? 'Sólo para cobros que llevan factura'
                    : 'Pide autorización a un administrador para emitir la factura antes de recibir el pago'
                }
              />
            )}
          </div>

          {/* El candado del toggle solo no se explica: sin esto parecía un
              bug que el efectivo viniera tildado y sin poder destildarse. */}
          {medio && ventaExigeFactura && !medioFuerzaFactura && (
            <p className="text-xs text-muted-foreground">
              Esta venta no tiene saldo para cobrar sin factura, así que el pago lleva
              factura.
            </p>
          )}

          {excedido && (
            <div className="rounded-xl border border-destructive/40 bg-destructive/10 px-3 py-2 text-xs text-destructive">
              <p className="font-medium">
                {isExento ? 'Monto excedido' : 'Monto excedido para facturar'}
              </p>
              <p className="mt-0.5 leading-snug">
                {isExento
                  ? `El monto supera el total de la venta (${formatMoney(disponibleAFacturar, ventaMoneda)}). Reducí el monto.`
                  : medioFuerzaFactura
                    ? `Este método de pago debe llevar factura de manera obligatoria y el monto supera lo disponible (${formatMoney(remainingFactura, ventaMoneda)}). Reducí el monto antes de continuar.`
                    : ventaExigeFactura
                      ? `Esta venta no admite cobros sin factura y el monto supera lo disponible para facturar (${formatMoney(remainingFactura, ventaMoneda)}). Reducí el monto antes de continuar.`
                      : `El monto supera lo disponible para facturar (${formatMoney(remainingFactura, ventaMoneda)}). Reducí el monto o desmarcá Lleva Factura.`}
              </p>
            </div>
          )}

          {excedidoSinFactura && (
            <div className="rounded-xl border border-destructive/40 bg-destructive/10 px-3 py-2 text-xs text-destructive">
              <p className="font-medium">Monto excedido sin factura</p>
              <p className="mt-0.5 leading-snug">
                {`El monto supera lo que esta venta admite cobrar sin factura (${formatMoney(remainingSinFactura, ventaMoneda)}). Reducí el monto o marcá Lleva Factura.`}
              </p>
            </div>
          )}

          {contieneFactura && <FiscalDataMissingWarning missing={clienteFiscalMissing} />}

          {/* Row 3 — comprobante upload + agregar (only for kind='pago';
              promesas don't carry a comprobante at registration). */}
          {!isPromesa && (
            <div className="space-y-2">
              <div className="flex items-center gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={triggerFilePick}
                disabled={loading}
                className="flex-1 gap-2 justify-start"
              >
                <Upload className="h-4 w-4 shrink-0" aria-hidden />
                <span className="truncate">
                  {file ? file.name : 'Subir comprobante'}
                </span>
              </Button>
              {file && (
                <>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    onClick={() => setPreview(file)}
                    disabled={loading}
                    aria-label="Ver comprobante"
                    title="Ver comprobante"
                  >
                    <Eye className="h-4 w-4" aria-hidden />
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    onClick={() => setFile(null)}
                    disabled={loading}
                    className="text-muted-foreground hover:text-destructive"
                    aria-label="Quitar comprobante"
                    title="Quitar comprobante"
                  >
                    <Trash2 className="h-4 w-4" aria-hidden />
                  </Button>
                </>
              )}
              <Button
                type="button"
                onClick={handleAdd}
                disabled={loading || !canStageButton}
                size="icon"
                className="bg-foreground text-background hover:bg-foreground/90"
                title={
                  !file
                    ? 'Subí un comprobante antes de agregar el pago'
                    : excedido
                      ? isExento
                        ? 'El monto supera el total de la venta'
                        : 'El monto supera lo disponible para facturar'
                      : excedidoSinFactura
                        ? 'El monto supera lo disponible sin factura'
                        : !canStage
                          ? 'Completá todos los campos del pago'
                          : 'Agregar pago a la lista'
                }
                aria-label="Agregar pago"
              >
                <Plus className="h-4 w-4" aria-hidden />
              </Button>
              <input
                ref={fileInputRef}
                type="file"
                accept="application/pdf,image/png,image/jpeg"
                hidden
                onChange={onFileInputChange}
              />
            </div>
            {fileError && <p className="text-xs text-destructive">{fileError}</p>}
            <div className="rounded-xl border bg-card">
              {queue.length === 0 ? (
                <div className="flex items-center justify-center gap-2 px-4 py-12 text-sm text-muted-foreground">
                  <FileText className="h-4 w-4" aria-hidden />
                  Agregue pagos para visualizarlos aquí
                </div>
              ) : (
                <ul className="divide-y">
                  {queue.map((p) => (
                    <li
                      key={p.key}
                      className="flex items-center gap-3 px-4 py-2 text-sm"
                    >
                      <FileText className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden />
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2">
                          <p className="truncate font-medium">{p.medioPago}</p>
                          <span className="text-muted-foreground">·</span>
                          <p className="tabular-nums">
                            {formatMoney(
                              p.monto,
                              p.categoria === 'Internacional' ? 'USD' : homeCurrency,
                            )}
                          </p>
                          {p.esPagoFinal && (
                            <span className="rounded-md bg-emerald-100 px-1.5 py-0.5 text-[10px] font-semibold text-emerald-700">
                              FINAL
                            </span>
                          )}
                          {p.contieneFactura && (
                            <span className="rounded-md bg-sky-100 px-1.5 py-0.5 text-[10px] font-semibold text-sky-700">
                              FC
                            </span>
                          )}
                        </div>
                        {p.echeq && (
                          <p className="truncate text-[11px] text-foreground/80">
                            N° {p.echeq.nroCheque} · {p.echeq.bancoEmisor} · CUIT{' '}
                            {p.echeq.cuitLibrador} · Pago{' '}
                            {p.echeq.fechaPago.split('-').reverse().join('/')}
                          </p>
                        )}
                        <p className="text-[11px] text-muted-foreground">
                          {p.fechaPago}
                          {p.file ? ` · ${p.file.name}` : ''}
                        </p>
                      </div>
                      <div className="flex shrink-0 items-center gap-0.5">
                        {p.file && (
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            onClick={() => p.file && setPreview(p.file)}
                            disabled={loading}
                            aria-label={`Ver comprobante de ${p.medioPago}`}
                            title="Ver comprobante"
                          >
                            <Eye className="h-4 w-4" aria-hidden />
                          </Button>
                        )}
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          onClick={() => triggerReplaceStagedFile(p.key)}
                          disabled={loading}
                          aria-label={`Reemplazar comprobante de ${p.medioPago}`}
                          title="Reemplazar comprobante"
                        >
                          <Pencil className="h-4 w-4" aria-hidden />
                        </Button>
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          onClick={() => removeStaged(p.key)}
                          disabled={loading}
                          className="text-muted-foreground hover:text-destructive"
                          aria-label={`Quitar pago ${p.medioPago}`}
                        >
                          <Trash2 className="h-4 w-4" aria-hidden />
                        </Button>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </div>
            </div>
          )}
        </DialogBody>
        <DialogFooter>
          <Button type="button" variant="outline" onClick={onCancel} disabled={loading}>
            Cancelar
          </Button>
          <Button
            type="button"
            onClick={handleRegistrar}
            disabled={!canRegistrar || loading}
            className="gap-2"
          >
            Registrar
          </Button>
        </DialogFooter>
      </DialogContent>
      {preview && <FilePreviewDialog file={preview} onClose={() => setPreview(null)} />}
    </Dialog>
  );
}

/**
 * Preview the staged comprobante (image or PDF) using a blob URL. The
 * URL is created on mount and revoked on unmount so we don't leak
 * memory if the user opens preview repeatedly.
 */
function FilePreviewDialog({
  file,
  onClose,
}: {
  file: File;
  onClose: () => void;
}) {
  const [blobUrl, setBlobUrl] = useState<string | null>(null);
  useEffect(() => {
    const url = URL.createObjectURL(file);
    setBlobUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [file]);

  const isImage = file.type.startsWith('image/');
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 truncate">
            <FileText className="h-4 w-4 shrink-0" aria-hidden />
            <span className="truncate">{file.name}</span>
          </DialogTitle>
        </DialogHeader>
        <DialogBody>
          {!blobUrl ? (
            <div className="flex h-64 items-center justify-center text-muted-foreground">
              <Loader2 className="h-5 w-5 animate-spin" aria-hidden />
            </div>
          ) : isImage ? (
            <img
              src={blobUrl}
              alt={file.name}
              className="mx-auto max-h-[70vh] w-auto rounded-lg"
            />
          ) : (
            <iframe
              src={blobUrl}
              title={file.name}
              className="h-[70vh] w-full rounded-lg border"
            />
          )}
        </DialogBody>
        <DialogFooter>
          <Button type="button" variant="outline" onClick={onClose} className="gap-2">
            <X className="h-4 w-4" aria-hidden />
            Cerrar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Field({
  label,
  children,
}: {
  label: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <div className="space-y-1">
      <Label className="text-sm">{label}</Label>
      {children}
    </div>
  );
}

function CheckboxRow({
  checked,
  onChange,
  label,
  disabled,
  title,
}: {
  checked: boolean;
  onChange: (next: boolean) => void;
  label: string;
  disabled?: boolean;
  /** Optional native tooltip — used to explain why a forced toggle
   *  (Banco rows with lleva_factura=true) can't be unchecked. */
  title?: string;
}) {
  return (
    <button
      type="button"
      role="checkbox"
      aria-checked={checked}
      disabled={disabled}
      onClick={() => onChange(!checked)}
      title={title}
      className={cn(
        'inline-flex items-center gap-2 text-sm transition-colors disabled:opacity-50',
        checked ? 'text-foreground' : 'text-muted-foreground',
      )}
    >
      <span
        className={cn(
          'flex h-5 w-5 items-center justify-center rounded border',
          checked ? 'border-emerald-500 bg-emerald-500 text-white' : 'border-border bg-background',
        )}
      >
        {checked && (
          <svg className="h-3.5 w-3.5" viewBox="0 0 20 20" fill="currentColor" aria-hidden>
            <path
              fillRule="evenodd"
              d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
              clipRule="evenodd"
            />
          </svg>
        )}
      </span>
      {label}
    </button>
  );
}

function sanitizeDecimal(value: string): string {
  const cleaned = value.replace(/[^\d.,-]/g, '');
  const match = cleaned.match(/^-?\d*([.,]\d*)?/);
  return match ? match[0] : '';
}

function todayIso(dias = 0): string {
  const d = new Date();
  d.setDate(d.getDate() + dias);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

function isoToAr(iso: string): string {
  const [y, m, d] = iso.split('-');
  if (!y || !m || !d) return iso;
  return `${d}/${m}/${y}`;
}

function round2(n: number): number {
  return Math.round(n * 100) / 100;
}

function cryptoKey(): string {
  // Simple unique-enough key for the local queue. crypto.randomUUID is
  // available everywhere we run, but we fall back for older runtimes.
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
    return crypto.randomUUID();
  }
  return `${Date.now()}-${Math.random().toString(36).slice(2)}`;
}
